import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { Typography } from '@components/Typography';
import ImportValidationTable from '@components/ImportValidationTable';
import Button from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import Autocomplete from '@components/Autocomplete';
import { CSVToArray } from '@helpers/csv-helper';
import {
    validateBulkData,
    removeUnchangedContacts,
    findDuplicate,
    importFieldEquals,
} from '@helpers/bulk-contacts-upload';
import { isBirthDateShaped, isLanguageShaped, isSexShaped } from '@helpers/contact-demographics';
import { alertActions, contactsActions } from '@actions';
import { contactsService } from '@services';
import { useTranslation, useContactLocations } from '@hooks';
import { handleApiError } from '@helpers/api';
import { Modal, FileInputContainer } from './styles';

const baseStyles = { maxWidth: '222px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const ContactsColumnNames = t => {
    return [
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.firstName')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.lastName')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.phoneNumber')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.emailAddress')}
                </Typography>
            ),
        },
        {
            key: 6,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.facebookProfile')}
                </Typography>
            ),
        },
        {
            key: 7,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.instagramProfile')}
                </Typography>
            ),
        },
        {
            key: 8,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.externalId')}
                </Typography>
            ),
        },
        {
            key: 9,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.sex')}
                </Typography>
            ),
        },
        {
            key: 10,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.dateOfBirth')}
                </Typography>
            ),
        },
        {
            key: 11,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.language')}
                </Typography>
            ),
        },
        {
            key: 12,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const cell = (isChanged, oldValue, newValue, resultColor) => {
    return !isChanged ? (
        <Typography color={resultColor} variant="body2" align="left" style={{ ...baseStyles }}>
            {newValue || '--'}
        </Typography>
    ) : (
        <>
            <Typography
                color={resultColor}
                variant="body2"
                align="left"
                style={{
                    ...baseStyles,
                    textDecoration: 'line-through',
                }}
            >
                {oldValue || '--'}
            </Typography>
            <Typography color={'success'} variant="body2" align="left" style={{ ...baseStyles }}>
                {newValue || '--'}
            </Typography>
        </>
    );
};

const DEMOGRAPHICS_CHECKS = {
    sex: isSexShaped,
    dateOfBirth: isBirthDateShaped,
    language: isLanguageShaped,
};

const CELL_COLORS = {
    phoneNumber: contact => (contact.phoneValid || !contact.phoneNumber ? 'textPrimary' : 'error'),
    email: contact => (contact.emailValid || !contact.email ? 'textPrimary' : 'error'),
    ...Object.fromEntries(
        Object.entries(DEMOGRAPHICS_CHECKS).map(([key, isShaped]) => [
            key,
            contact => (isShaped(contact[key]) ? 'textPrimary' : 'error'),
        ]),
    ),
};

const contactRows = (model, onDelete) =>
    model.contacts.map((contact, index) => {
        const duplicate = findDuplicate(contact, model.duplicates);
        const resultColor = 'textPrimary';
        const changed = {
            firstName: false,
            lastName: false,
            phoneNumber: false,
            email: false,
            facebook: false,
            instagram: false,
            externalId: false,
            sex: false,
            dateOfBirth: false,
            language: false,
        };
        if (duplicate && contact.phoneValid && contact.emailValid) {
            Object.entries(changed).forEach(([key]) => {
                if (contact[key] && !importFieldEquals(key, duplicate[key], contact[key])) {
                    changed[key] = true;
                }
                if (!changed[key] && duplicate[key]) {
                    contact[key] = duplicate[key];
                }
            });
        }
        const components = [];
        Object.entries(changed).forEach(([key, value]) => {
            const color = CELL_COLORS[key]?.(contact) ?? resultColor;
            components.push({
                component: cell(value, duplicate?.[key], contact[key], color),
            });
        });
        components.push({
            component: (
                <IconButton color={!isContactValid(contact) ? 'error' : 'primary'} onClick={() => onDelete(index)}>
                    <DeleteIcon />
                </IconButton>
            ),
        });
        return components;
    });

const isContactValid = c =>
    c.demographicsValid !== false && ((c.emailValid && c.phoneValid) || (c.emailValid && !c.phoneNumber));

const CsvLink = 'https://docs.google.com/spreadsheets/d/1elW5JQyXmByeMzRHhhsSuX3PXL42HyxGhNytbWXfVsI/';

const BulkUploadModal = ({ isOpen, setModal, locale, onUpdateCallback }) => {
    const { t } = useTranslation();
    const { getLocationsOptions } = useContactLocations();
    const [modalState, setModalState] = useState({ isExpectingFile: true, showContactList: false });
    const [importModel, setImportModel] = useState({ contacts: [], duplicates: [] });
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [fileName, setFileName] = useState('');
    const [hasConsent, setHasConsent] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(contactsActions.getLocations());
    }, [dispatch]);

    const locationsOptions = useMemo(() => getLocationsOptions(), [getLocationsOptions]);

    const isListValid = useMemo(() => {
        return importModel.contacts.length > 0 && importModel.contacts.every(isContactValid);
    }, [importModel.contacts]);

    const onCloseModal = () => setModal(false);

    const onDeleteContact = newValue => {
        const contactsCopy = [...importModel.contacts];
        contactsCopy.splice(newValue, 1);
        setImportModel(prev => ({ ...prev, contacts: contactsCopy }));
    };

    const submitCSV = event => {
        const file = event.target.files[0];
        setFileName(file.name);
        setHasConsent(false);
        let fileReader = new FileReader();
        const processData = async () => {
            const data = fileReader.result;
            const list = validateBulkData(CSVToArray(data), locale);
            if (!list || list.length === 0) {
                dispatch(alertActions.error(t('validation.wrongFile')));
            } else {
                const savedContacts = await getDuplicates(list);
                const response = removeUnchangedContacts(list, savedContacts);
                setImportModel(response);
                setModalState(prev => ({ ...prev, isExpectingFile: false }));
            }
        };
        fileReader.onloadend = processData;
        fileReader.readAsText(file);

        event.target.value = null;
    };

    const getDuplicates = async contacts => {
        try {
            const data = await contactsService.getDuplicates(contacts);
            return data;
        } catch (error) {
            handleApiError(error);
        }
    };

    const importContacts = () => setModalState(prev => ({ ...prev, showContactList: true }));

    const uploadContacts = async () => {
        try {
            const locations = selectedLocations.map(location => location.id);
            const contactsUpdated = importModel.contacts.map(contact => {
                const duplicate = findDuplicate(contact, importModel.duplicates);
                if (duplicate) {
                    Object.entries(contact).forEach(([key]) => {
                        if (!contact[key] && duplicate[key]) {
                            contact[key] = duplicate[key];
                        }
                    });
                    return {
                        ...contact,
                        id: duplicate.id,
                        labels: duplicate.labels,
                        //companies: [...new Set([...duplicate.companies, ...locations])],
                    };
                }
                return contact; //{ ...contact, /*companies: locations */};
            });
            await contactsService.importContactsBulk({ contacts: contactsUpdated, locations });
            dispatch(alertActions.success(t('alertMessages.importSuccess')));
            onUpdateCallback();
        } catch (error) {
            handleApiError(error, 'alertMessages.importFail');
        }
        setModal(false);
    };

    return (
        <>
            <Modal
                fullScreen
                isOpen={isOpen}
                handleClose={onCloseModal}
                showContactList={modalState.showContactList}
                title={t('ContactsPage.importTitle')}
                subtitle={
                    <>
                        {t('ContactsPage.importSubtitle')}{' '}
                        <a href={CsvLink} target="_blank" rel="noreferrer" style={{ color: 'var(--primary-main)' }}>
                            {t('ContactsPage.bulkFileLink')}
                        </a>
                    </>
                }
                onPrimaryAction={!modalState.showContactList ? importContacts : uploadContacts}
                primaryActionText={!modalState.showContactList ? t('buttons.validate') : t('ContactsPage.importFile')}
                primaryActionDisabled={
                    selectedLocations.length === 0 || modalState.isExpectingFile
                        ? true
                        : modalState.showContactList
                          ? !isListValid || !hasConsent
                          : false
                }
                onSecondaryAction={
                    modalState.showContactList
                        ? () => {
                              setHasConsent(false);
                              setModalState(prev => ({ ...prev, showContactList: false }));
                          }
                        : null
                }
                secondaryActionText={t('buttons.back')}
            >
                {modalState.showContactList ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ marginBottom: '24px', display: 'block', color: 'var(--text-secondary)' }}
                        >
                            {t('ContactsPage.dataVerificationDescription')}
                        </Typography>
                        <ImportValidationTable
                            columnNames={ContactsColumnNames(t)}
                            rows={contactRows(importModel, onDeleteContact)}
                            hasError={importModel.contacts.map(c => !isContactValid(c))}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={hasConsent} onChange={e => setHasConsent(e.target.checked)} />}
                            label={<Typography variant="body2">{t('ContactsPage.importConsent')}</Typography>}
                            sx={{ marginTop: '16px', marginLeft: '-9px', marginRight: 0 }}
                        />
                    </>
                ) : (
                    <>
                        <Autocomplete
                            label={t('common.locations')}
                            options={locationsOptions}
                            value={selectedLocations}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{ style: { display: selectedLocations?.length > 0 ? 'none' : 'block' } }}
                            onChangeAutoComplete={items => setSelectedLocations(items.length ? items : [])}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                            }}
                            required
                            className="companies-autocomplete"
                        />
                        <Typography variant="caption" color="textSecondary">
                            {t('ContactsPage.csvFile')}
                        </Typography>
                        <FileInputContainer>
                            <input
                                accept=".csv,.xlsx,.xls"
                                style={{ display: 'none' }}
                                id="raised-button-file"
                                type="file"
                                onChange={submitCSV}
                                name="contactsList"
                            />
                            <label htmlFor="raised-button-file">
                                <Button type="button" component="span" fullWidth variant="outlined">
                                    {t('buttons.uploadCSVFile')}
                                </Button>
                            </label>
                            <Typography variant="caption" color="textSecondary">
                                {fileName}
                            </Typography>
                        </FileInputContainer>
                        {importModel.contacts?.length > 0 ? (
                            <Typography
                                variant="caption"
                                sx={{ color: 'var(--text-secondary)', marginTop: '8px', display: 'block' }}
                            >
                                {t('ContactsPage.contactsToUpdate')}: {importModel.duplicates?.length || 0}
                                {' · '}
                                {t('ContactsPage.contactsToAdd')}:{' '}
                                {importModel.contacts.length - (importModel.duplicates?.length || 0)}
                            </Typography>
                        ) : null}
                    </>
                )}
            </Modal>
        </>
    );
};

export default BulkUploadModal;
